import { get } from '@rover/react-lib/src/apis/systemInfo.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import type { DjangoSystemInfo } from '@rover/types';

export type State = {
  hasPrefetched: boolean;
  loading: boolean;
  djangoSystemInfo: DjangoSystemInfo | null;
  ssrHeaders: Record<string, string> | null;
  error: string;
};

type Actions = {
  prefetchDataPending: ActionCreator<void>;
  prefetchDataSuccess: ActionCreator<DjangoSystemInfo>;
  prefetchDataFailure: ActionCreator<string>;
  setSSRHeaders: ActionCreator<void>;
};

type Effects = {
  prefetchData: EffectCreator<void>;
};

type Selectors = {
  getSSRHeaders: SelectorCreator<Record<string, string> | null>;
  getDjangoSystemInfo: SelectorCreator<DjangoSystemInfo | null>;
  getSystemInfoError: SelectorCreator<string>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'systemInfo',
  initialState: {
    loading: false,
    hasPrefetched: false,
    djangoSystemInfo: null,
    ssrHeaders: null,
    error: '',
  },
  actions: {
    setSSRHeaders: () => (state) => {
      const req = httpContext.getReq();
      const headers = req?.headers as Record<string, string> | undefined;

      return {
        ...state,
        ssrHeaders: (process.env.JS_ENV_SERVER && headers) || null,
      };
    },
    prefetchDataPending: () => (state) => ({
      ...state,
      loading: true,
    }),
    prefetchDataSuccess: (djangoSystemInfo) => (state) => ({
      ...state,
      loading: false,
      hasPrefetched: true,
      djangoSystemInfo,
      error: '',
    }),
    prefetchDataFailure: (error) => (state) => ({
      ...state,
      loading: false,
      hasPrefetched: true,
      djangoSystemInfo: null,
      error,
    }),
  },
  effects: {
    prefetchData:
      () =>
      async (dispatch, getState, duckActions): Promise<void> => {
        dispatch(duckActions.prefetchDataPending());
        dispatch(duckActions.setSSRHeaders());
        try {
          const response: DjangoSystemInfo = await get();
          dispatch(duckActions.prefetchDataSuccess(response));
        } catch (e: any) {
          dispatch(duckActions.prefetchDataFailure(`ERROR:  ${e.message}`));
        }
      },
  },
  selectors: {
    getSSRHeaders: (getState, createSelector) =>
      createSelector([getState], (state) => state.ssrHeaders),
    getDjangoSystemInfo: (getState, createSelector) =>
      createSelector([getState], (state) => state.djangoSystemInfo),
    getSystemInfoError: (getState, createSelector) =>
      createSelector([getState], (state) => state.error),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
